import { z } from "zod";
import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@astryxdesign/core/Button";
import * as stylex from "@stylexjs/stylex";
import { accessAPI, queries } from "../lib/api";
import { RequestFailure } from "../components/RequestFailure";
import { isSyncFresh, useHealthClock } from "../lib/health";
import { styles } from "../lib/styles";
import { AccessTable } from "../components/AccessTable";
import { ServerForm } from "../components/ServerForm";
import { Drawer, PageHeader } from "../components/Workspace";

export const Route = createFileRoute("/servers/")({
  validateSearch: z.object({
    q: z.string().max(200).optional().catch(undefined),
    auth: z.enum(["all", "ready", "attention"]).optional().catch(undefined),
    page: z.coerce.number().int().min(0).max(100000).optional().catch(undefined),
  }),
  component: ServersPage,
});
function ServersPage() {
  const me = useQuery(queries.me());
  const now = useHealthClock();
  const [notice, setNotice] = useState("");
  const state = useQuery({ ...queries.state(), enabled: me.data?.admin === true });
  const [adding, setAdding] = useState(false);
  const cache = useQueryClient();
  const sync = useMutation({
    mutationFn: accessAPI.reconcile,
    onSuccess: () => cache.invalidateQueries({ queryKey: ["access"] }),
  });
  if (!me.data?.admin)
    return (
      <p {...stylex.props(styles.notice)}>Administrator access is required to manage servers.</p>
    );
  return (
    <>
      <PageHeader
        title="Servers"
        description="Manage server accounts and see whether access is ready."
        action={
          <>
            <Button
              label="Synchronize"
              isDisabled={!!state.error || state.isPending}
              onClick={() => sync.mutate()}
              isLoading={sync.isPending}
            />
            <Button
              label="Add server"
              variant="primary"
              isDisabled={!!state.error || state.isPending}
              onClick={() => setAdding(true)}
            />
          </>
        }
      />
      <RequestFailure
        error={state.error}
        retry={() => {
          void state.refetch();
        }}
        pending={state.isFetching}
      />
      <RequestFailure error={sync.error} />
      {notice && (
        <output {...stylex.props(styles.notice)}>
          {notice} <a href="/rules">Manage access rules</a>
        </output>
      )}
      {sync.isSuccess && (
        <output {...stylex.props(styles.notice)}>
          Synchronization queued. Applied access will update after verification.
        </output>
      )}
      {state.isPending && (
        <p aria-busy="true" {...stylex.props(styles.notice)}>
          Loading servers…
        </p>
      )}
      {state.data && (
        <>
          <div className="table-toolbar">
            <div className="inline-health">
              <span className={`health-dot${!isSyncFresh(state.data, now) ? " unhealthy" : ""}`} />
              {state.data.sync_error
                ? "Synchronization needs attention"
                : isSyncFresh(state.data, now)
                  ? `Synced ${new Date(state.data.last_sync!).toLocaleString()}`
                  : state.data.last_sync
                    ? "Synchronization is stale. Access readiness is unverified until the worker catches up."
                    : "Awaiting first synchronization"}
            </div>
            <span {...stylex.props(styles.small)}>
              {state.data.servers.length} registered accounts
            </span>
          </div>
          {state.data.sync_error && (
            <p role="alert" {...stylex.props(styles.error)}>
              {state.data.sync_error}
            </p>
          )}
          <section data-workspace-panel {...stylex.props(styles.panel)}>
            <AccessTable state={state.data} readOnly={!!state.error} />
          </section>
        </>
      )}
      {adding && (
        <Drawer
          title="Add server"
          description="Register an existing NetBird peer for managed SSH access."
          onClose={() => setAdding(false)}
        >
          <ServerForm
            onSaved={(server) => {
              setNotice(
                `${server.alias} was added. Add an access rule, then synchronize before connecting.`,
              );
              setAdding(false);
            }}
          />
        </Drawer>
      )}
    </>
  );
}
